import {
  MessageCircle, Mail, Instagram, Facebook, Linkedin, Send, Phone, Mic, Voicemail, Bot,
} from "lucide-react";

export const channelMeta = {
  whatsapp:  { label: "WhatsApp",  cls: "ch-whatsapp",  Icon: MessageCircle },
  email:     { label: "Email",     cls: "ch-email",     Icon: Mail },
  instagram: { label: "Instagram", cls: "ch-instagram", Icon: Instagram },
  facebook:  { label: "Facebook",  cls: "ch-facebook",  Icon: Facebook },
  linkedin:  { label: "LinkedIn",  cls: "ch-linkedin",  Icon: Linkedin },
  telegram:  { label: "Telegram",  cls: "ch-telegram",  Icon: Send },
  x:         { label: "X",         cls: "ch-x",         Icon: Send },
  wechat:    { label: "WeChat",    cls: "ch-wechat",    Icon: MessageCircle },
  viber:     { label: "Viber",     cls: "ch-viber",     Icon: Phone },
  voice:     { label: "Voice",     cls: "ch-voice",     Icon: Phone },
  voicemail: { label: "Voicemail", cls: "ch-voicemail", Icon: Voicemail },
  system:    { label: "System",    cls: "ch-system",    Icon: Bot },
};

export const intentMeta = {
  high_intent: { label: "High Intent", cls: "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20" },
  follow_up:   { label: "Follow Up",   cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20" },
  sale_intent: { label: "Sale Intent", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20" },
  new_lead:    { label: "New Lead",    cls: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20" },
};

// All backend timestamps are stored in UTC (ISO with +00:00). The UI must
// render them in the operator's timezone — defaults to IST (Asia/Kolkata) and
// can be overridden at runtime via window.__C7_TZ__ or localStorage 'c7_tz'.
export const DISPLAY_TZ =
  (typeof window !== "undefined" && (window.__C7_TZ__ || window.localStorage?.getItem("c7_tz"))) ||
  "Asia/Kolkata";

// Date parts (yyyy-mm-dd) for a given instant, evaluated in DISPLAY_TZ — used
// for "same day / yesterday" comparisons so they don't drift across midnight.
function _ymd(d) {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: DISPLAY_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 30) return "Just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return fmtDate(iso);
}

export function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], {
    timeZone: DISPLAY_TZ, hour: "2-digit", minute: "2-digit",
  });
}

// "2026-06-17" rendered in DISPLAY_TZ (IST).
export function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], {
    timeZone: DISPLAY_TZ, year: "numeric", month: "short", day: "numeric",
  });
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const dymd = _ymd(d);
  const yest = new Date(now); yest.setDate(yest.getDate() - 1);
  const t = d.toLocaleTimeString([], { timeZone: DISPLAY_TZ, hour: "2-digit", minute: "2-digit" });
  if (dymd === _ymd(now)) return `Today ${t}`;
  if (dymd === _ymd(yest)) return `Yesterday ${t}`;
  return d.toLocaleDateString([], { timeZone: DISPLAY_TZ, month: "short", day: "numeric" }) + " " + t;
}

// Full "YYYY-MM-DD HH:mm:ss" in DISPLAY_TZ (IST) for report tables.
export function fmtStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone: DISPLAY_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
  const time = d.toLocaleTimeString("en-GB", {
    timeZone: DISPLAY_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  return `${date} ${time}`;
}

export function initials(name = "") {
  return name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}


// Start a manual dial from anywhere (Contacts, callbacks, CRM card). Routes
// the call through the always-mounted Softphone's manual-dial flow (gated by
// the header campaign, labeled "Manual", recorded) AND opens the agent
// workspace so the CRM lead-pop + omnichannel panels appear. We wait for the
// workspace to signal it's mounted (c7:workspace-ready) before firing the dial
// so the screen-pop listener never misses the event (with a safety fallback).
let _c7WorkspaceReady = false;
if (typeof window !== "undefined") {
  window.addEventListener("c7:workspace-ready", () => { _c7WorkspaceReady = true; });
  window.addEventListener("c7:workspace-unmount", () => { _c7WorkspaceReady = false; });
}

// Run `fire` once the agent workspace is mounted (so its screen-pop / dial
// listeners are attached), navigating there first if needed, with a safety
// fallback if the ready signal is missed.
function _whenWorkspaceReady(fire, navigate) {
  if (_c7WorkspaceReady) { fire(); return; }
  if (navigate) navigate("/workspace");
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    window.removeEventListener("c7:workspace-ready", finish);
    fire();
  };
  window.addEventListener("c7:workspace-ready", finish);
  setTimeout(finish, 1500);
}

export function startManualDial(phone, name, navigate) {
  const p = String(phone || "").trim();
  if (!p) return false;
  _whenWorkspaceReady(() => window.dispatchEvent(
    new CustomEvent("c7:dial-number", { detail: { phone: p, name: name || "" } })), navigate);
  return true;
}

// Fire the CRM screen-pop for an active call, opening the workspace first if
// the agent is on another page (e.g. typed a number on the dialpad while
// viewing Call logs / Contacts). Keeps the lead-pop reliable across navigation.
export function popScreenOnWorkspace(detail, navigate) {
  _whenWorkspaceReady(() => window.dispatchEvent(
    new CustomEvent("dialer:bridged", { detail: detail || {} })), navigate);
}

// Convert a hex color (#RGB or #RRGGBB) to a space-separated HSL triplet
// ("H S% L%") suitable for a Tailwind/shadcn `hsl(var(--x))` CSS variable.
export function hexToHslTriplet(hex) {
  if (!hex) return null;
  let h = String(hex).replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh, s; const l = (max + min) / 2;
  if (max === min) { hh = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      default: hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
